import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { API_BASE_URL } from "../environment";
import { updateChatIntegrationStatus } from "../utils/chatIntegration";
import { LeadService } from "../feature-module/crm/leads/lead.service";
import {
  readStoredCurrencyCode,
  storeCurrencyCode,
  useStoredCurrencyCode,
} from "../components/currencyFormat";

interface AuthContextType {
  auth: any;
  setAuth: (authData: any) => void;
  selectedTenantId: string | null;
  setSelectedTenantId: (id: string | null) => void;
  logout: () => void;
  lifecycleTypeNames: Record<string, string>;
  setLifecycleTypeNames: (names: Record<string, string>) => void;
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  importantUnreadCount: number;
  setImportantUnreadCount: (count: number) => void;
  updateEmailCounts: (unread: number, important: number) => void;
  isTeamManager: boolean;
  setIsTeamManager: (isManager: boolean) => void;
  currencyCode: string | null;
}

export const AuthContext = createContext<AuthContextType>({
  auth: null,
  setAuth: () => { },
  selectedTenantId: null,
  setSelectedTenantId: () => { },
  logout: () => { },
  lifecycleTypeNames: {},
  setLifecycleTypeNames: () => { },
  unreadCount: 0,
  setUnreadCount: () => { },
  importantUnreadCount: 0,
  setImportantUnreadCount: () => { },
  updateEmailCounts: () => { },
  isTeamManager: false,
  setIsTeamManager: () => { },
  currencyCode: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [lifecycleTypeNames, setLifecycleTypeNames] = useState<
    Record<string, string>
  >(() => JSON.parse(localStorage.getItem("lifecycleTypeNames") || "{}"));

  const logout = useCallback(() => {
    setAuth(null);
    setSelectedTenantId(null);
    setLifecycleTypeNames({});
    setIsTeamManager(false);
    localStorage.removeItem("authResponse");
    localStorage.removeItem("authTimestamp");
    localStorage.removeItem("authExpiryTime");
    localStorage.removeItem("lastActivityTime");
    localStorage.removeItem("selectedTenantId");
    localStorage.removeItem("authCred");
    localStorage.removeItem("lifecycleTypeNames");
    localStorage.removeItem("avatar_url");
    localStorage.removeItem("isTeamManager");
    localStorage.removeItem("crmViewStates");
    localStorage.removeItem("currency_code");

    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("filter_state_")) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn("Error clearing filter states:", error);
    }
  }, []);

  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    localStorage.setItem("lastActivityTime", now.toString());
    const newExpiryTime = now + 24 * 60 * 60 * 1000;
    localStorage.setItem("authExpiryTime", newExpiryTime.toString());
  }, []);

  const [auth, setAuth] = useState<any>(() => {
    const stored = localStorage.getItem("authResponse");
    if (!stored) return null;

    const lastActivityTime = localStorage.getItem("lastActivityTime");
    if (lastActivityTime) {
      const timeSinceLastActivity = Date.now() - Number(lastActivityTime);
      if (timeSinceLastActivity > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("authResponse");
        localStorage.removeItem("authTimestamp");
        localStorage.removeItem("authExpiryTime");
        localStorage.removeItem("lastActivityTime");
        localStorage.removeItem("selectedTenantId");
        localStorage.removeItem("authCred");
        localStorage.removeItem("lifecycleTypeNames");
        localStorage.removeItem("avatar_url");
        localStorage.removeItem("isTeamManager");
        return null;
      }
    } else {
      const expiryTime = localStorage.getItem("authExpiryTime");
      if (expiryTime) {
        const now = Date.now();
        const expiry = Number(expiryTime);

        if (now >= expiry) {
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("authExpiryTime");
          localStorage.removeItem("lastActivityTime");
          localStorage.removeItem("selectedTenantId");
          localStorage.removeItem("authCred");
          localStorage.removeItem("lifecycleTypeNames");
          localStorage.removeItem("avatar_url");
          localStorage.removeItem("isTeamManager");
          return null;
        }
      } else {
        const ts = localStorage.getItem("authTimestamp");
        if (ts) {
          const age = Date.now() - Number(ts);
          if (age > 24 * 60 * 60 * 1000) {
            localStorage.removeItem("authResponse");
            localStorage.removeItem("authTimestamp");
            localStorage.removeItem("authExpiryTime");
            localStorage.removeItem("lastActivityTime");
            localStorage.removeItem("isTeamManager");
            return null;
          }
        }
      }
    }

    const authData = JSON.parse(stored);

    const isSuperAdmin =
      authData?.tenants?.some(
        (t: any) =>
          t.role_key?.toLowerCase() === "superadmin" ||
          t.role_key?.toLowerCase() === "super_admin" ||
          t.role_name?.toLowerCase() === "superadmin" ||
          t.role_name?.toLowerCase() === "super_admin" ||
          t.role_key?.toLowerCase() === "platform_analyst" ||
          t.role_name?.toLowerCase() === "platformanalyst" ||
          t.role_key?.toLowerCase() === "apiadmin" ||
          t.role_key?.toLowerCase() === "api_admin" ||
          t.role_name?.toLowerCase() === "apiadmin" ||
          t.role_name?.toLowerCase() === "api_admin"
      ) || false;

    if (isSuperAdmin && !localStorage.getItem("selectedTenantId")) {
      localStorage.removeItem("selectedTenantId");
    }

    return authData;
  });

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    () => {
      const storedId = localStorage.getItem("selectedTenantId");
      if (storedId) return storedId;

      return null;
    }
  );

  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [importantUnreadCount, setImportantUnreadCount] = useState<number>(0);
  const [isTeamManager, setIsTeamManager] = useState<boolean>(() => {
    const stored = localStorage.getItem("isTeamManager");
    return stored ? JSON.parse(stored) : false;
  });
  const currencyCode = useStoredCurrencyCode(selectedTenantId);
  const previousTenantIdRef = useRef<string | null>(selectedTenantId);

  useEffect(() => {
    if (previousTenantIdRef.current === selectedTenantId) return;
    previousTenantIdRef.current = selectedTenantId;
    if (!selectedTenantId) return;

    const authCred = localStorage.getItem("authCred");
    if (!authCred) return;

    let cancelled = false;
    fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${authCred}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.currency_code) {
          storeCurrencyCode(data.currency_code);
        }
      })
      .catch(() => { });

    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const updateEmailCounts = (unread: number, important: number) => {
    setUnreadCount(unread);
    setImportantUnreadCount(important);
  };

  useEffect(() => {
    if (!auth) return;

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      updateLastActivity();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    const checkExpiry = () => {
      const lastActivityTime = localStorage.getItem("lastActivityTime");
      if (lastActivityTime) {
        const timeSinceLastActivity = Date.now() - Number(lastActivityTime);
        if (timeSinceLastActivity > 24 * 60 * 60 * 1000) {
          logout();
        }
      }
    };

    const checkStorageCleared = () => {
      if (auth && !localStorage.getItem("authResponse")) {
        logout();
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authResponse" && e.newValue === null && auth) {
        logout();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const intervalId = setInterval(() => {
      checkExpiry();
      checkStorageCleared();
    }, 1000);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, [auth, logout, updateLastActivity]);

  useEffect(() => {
    if (!auth?.tenants) return;

    const isSuperAdmin =
      auth.tenants.some(
        (t: any) =>
          t.role_key?.toLowerCase() === "superadmin" ||
          t.role_key?.toLowerCase() === "super_admin" ||
          t.role_name?.toLowerCase() === "superadmin" ||
          t.role_name?.toLowerCase() === "super_admin" ||
          t.role_key?.toLowerCase() === "platform_analyst" ||
          t.role_name?.toLowerCase() === "platformanalyst" ||
          t.role_key?.toLowerCase() === "apiadmin" ||
          t.role_key?.toLowerCase() === "api_admin" ||
          t.role_name?.toLowerCase() === "apiadmin" ||
          t.role_name?.toLowerCase() === "api_admin"
      ) || false;

    if (isSuperAdmin && !selectedTenantId) {
      return;
    }

    if (!selectedTenantId && auth.tenants.length > 0) {
      setSelectedTenantId(String(auth.tenants[0].tenant_id));
    }
  }, [auth?.tenants, selectedTenantId]);

  useEffect(() => {
    if (auth) {
      const currentTime = Date.now();
      const expiryTime = currentTime + 24 * 60 * 60 * 1000;

      localStorage.setItem("authResponse", JSON.stringify(auth));
      localStorage.setItem("authTimestamp", currentTime.toString());
      localStorage.setItem("authExpiryTime", expiryTime.toString());
      localStorage.setItem("lastActivityTime", currentTime.toString());

      const currentTenant =
        auth.tenants?.find(
          (t: any) => String(t.tenant_id) === String(selectedTenantId)
        ) || auth.tenants?.[0];
      if (currentTenant?.team_manager !== undefined && isTeamManager !== Boolean(currentTenant.team_manager)) {
        setIsTeamManager(Boolean(currentTenant.team_manager));
      }
      const currencyFromAuth =
        currentTenant?.lifecycle_types?.find((lt: any) => lt.currency_code)
          ?.currency_code ||
        currentTenant?.lifecycle_types?.[0]?.currency_code;
      if (currencyFromAuth && !readStoredCurrencyCode()) {
        storeCurrencyCode(currencyFromAuth);
      }

      const isSuperAdmin =
        auth.tenants?.some(
          (t: any) =>
            t.role_key?.toLowerCase() === "superadmin" ||
            t.role_key?.toLowerCase() === "super_admin" ||
            t.role_name?.toLowerCase() === "superadmin" ||
            t.role_name?.toLowerCase() === "super_admin" ||
            t.role_key?.toLowerCase() === "platform_analyst" ||
            t.role_name?.toLowerCase() === "platformanalyst" ||
            t.role_key?.toLowerCase() === "apiadmin" ||
            t.role_key?.toLowerCase() === "api_admin" ||
            t.role_name?.toLowerCase() === "apiadmin" ||
            t.role_name?.toLowerCase() === "api_admin"
        ) || false;

      if (isSuperAdmin && !selectedTenantId) {
        localStorage.removeItem("selectedTenantId");
      }
    } else {
      localStorage.removeItem("authResponse");
      localStorage.removeItem("authTimestamp");
      localStorage.removeItem("authExpiryTime");
      localStorage.removeItem("lastActivityTime");
    }

    if (selectedTenantId) {
      localStorage.setItem("selectedTenantId", selectedTenantId);
    } else {
      localStorage.removeItem("selectedTenantId");
    }
    localStorage.setItem(
      "lifecycleTypeNames",
      JSON.stringify(lifecycleTypeNames)
    );
    localStorage.setItem("isTeamManager", JSON.stringify(isTeamManager));
  }, [auth, selectedTenantId, lifecycleTypeNames, isTeamManager]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedFetch = () => {
      timeoutId = setTimeout(async () => {
        if (!selectedTenantId) return;

        const authCred = localStorage.getItem("authCred");
        if (!authCred) return;

        try {
          const response = await fetch(
            `${API_BASE_URL}/lifecycle-type/?tenant_id=${selectedTenantId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authCred}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            const lifecycleTypes = data.results || data;

            const names = lifecycleTypes.reduce(
              (map: Record<string, string>, item: any) => ({
                ...map,
                [item.lifecycle_type_key]: item.lifecycle_type_display_name,
              }),
              {}
            );

            setLifecycleTypeNames(names);

            const currencyCode = Array.isArray(lifecycleTypes)
              ? lifecycleTypes.find((item: any) => item.currency_code)?.currency_code ||
                lifecycleTypes[0]?.currency_code
              : null;
            if (currencyCode && !readStoredCurrencyCode()) {
              storeCurrencyCode(currencyCode);
            }
          }
        } catch (error) { }
      }, 500);
    };

    debouncedFetch();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedTenantId]);

  // Update chat integration status when tenant changes
  useEffect(() => {
    if (selectedTenantId) {
      updateChatIntegrationStatus(selectedTenantId);
      LeadService.evaluateEmailEngagement(parseInt(selectedTenantId, 10));
    } else {
      localStorage.setItem("chatIntegration", "false");
    }
    setUnreadCount(0);
    setImportantUnreadCount(0);
  }, [selectedTenantId]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedRefresh = () => {
      timeoutId = setTimeout(async () => {
        if (!auth?.user_id) return;
        const authCred = localStorage.getItem("authCred") || "";
        try {
          const res = await fetch(`${API_BASE_URL}/user/${auth.user_id}/`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authCred}`,
            },
          });
          if (res.ok) {
            const profile = await res.json();
            if (profile.avatar_url) {
              setAuth((prev: any) => ({
                ...prev,
                avatar_url: profile.avatar_url,
              }));
            }
          }
        } catch (e) { }
      }, 300);
    };

    debouncedRefresh();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [auth?.user_id]);

  const setAuthWithSuperAdminLogic = (authData: any) => {
    setAuth(authData);
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth: setAuthWithSuperAdminLogic,
        selectedTenantId,
        setSelectedTenantId,
        logout,
        lifecycleTypeNames,
        setLifecycleTypeNames,
        unreadCount,
        setUnreadCount,
        importantUnreadCount,
        setImportantUnreadCount,
        updateEmailCounts,
        isTeamManager,
        setIsTeamManager,
        currencyCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
